import { useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { BarChart3, CalendarDays, Check, ChevronDown, Database, Hourglass, Inbox, Lightbulb, MailPlus, MessageCircleReply, MessagesSquare, Plug, RefreshCcw } from 'lucide-react';
import clsx from 'clsx';
import type { CoverageReport, InboxInsights } from '@mailpilot/shared';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { fullDate, relativeTime, shortDate } from '../lib/format';
import { useMailboxMap } from '../hooks/useMailboxes';
import {
  AccountBadge,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Page,
  PageHeader,
  ProgressBar,
  Skeleton,
  SkeletonLines,
  spring,
  useToast,
  type Tone,
} from '../components/ui';
import { AskPanel } from '../components/insights/AskPanel';

type Period = '7' | '30' | '90' | 'custom';

function dayInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function startOfDayIso(v: string): string | null {
  const d = new Date(`${v}T00:00:00`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}
function endOfDayIso(v: string): string | null {
  const d = new Date(`${v}T23:59:59.999`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

const phaseMeta: Record<CoverageReport['mailboxes'][number]['phase'], { label: string; tone: Tone }> = {
  idle: { label: 'Not started', tone: 'neutral' },
  recent: { label: 'Importing recent mail', tone: 'info' },
  history: { label: 'Importing history', tone: 'info' },
  complete: { label: 'Import complete', tone: 'success' },
  paused: { label: 'Paused', tone: 'warn' },
  error: { label: 'Import error', tone: 'danger' },
};

const PERIODS: { value: Period; label: string }[] = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: 'custom', label: 'Custom' },
];

export function InsightsPage() {
  const today = dayInput(new Date());
  const [period, setPeriod] = useState<Period>('7');
  const [customFrom, setCustomFrom] = useState(() => dayInput(new Date(Date.now() - 14 * 86400_000)));
  const [customTo, setCustomTo] = useState(today);
  const fromId = useId();
  const toId = useId();

  const range = useMemo((): { from: string; to: string } | null => {
    if (period === 'custom') {
      const from = startOfDayIso(customFrom);
      const to = endOfDayIso(customTo);
      if (!from || !to || from > to) return null;
      return { from, to };
    }
    const days = Number(period);
    const from = startOfDayIso(dayInput(new Date(Date.now() - (days - 1) * 86400_000)));
    const to = endOfDayIso(today);
    return from && to ? { from, to } : null;
  }, [period, customFrom, customTo, today]);

  const q = useQuery({
    queryKey: qk.insights(range?.from ?? '', range?.to ?? ''),
    queryFn: () => api('GET /api/insights', { query: { from: range!.from, to: range!.to } }),
    enabled: !!range,
    placeholderData: (prev) => prev,
  });

  const data = q.data;
  const invalidRange = period === 'custom' && !range;

  return (
    <Page wide>
      <PageHeader
        tone="yellow"
        icon={<Lightbulb className="h-5 w-5" />}
        title="Insights"
        description="What needs you, what's stalled, and what your mail has been about — drawn only from messages MailPilot has imported."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div role="radiogroup" aria-label="Insights period" className="flex items-center gap-0.5 rounded-full border border-hairline bg-surface p-0.5 shadow-sm">
              {PERIODS.map((p) => (
                <button key={p.value} type="button" role="radio" aria-checked={period === p.value} className="mp-chip !h-8" onClick={() => setPeriod(p.value)}>
                  {p.value === 'custom' && <CalendarDays className="h-3.5 w-3.5" aria-hidden />}
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        }
      />

      <div className="space-y-5">
        {(period === 'custom' || data) && (
          <div className="flex flex-wrap items-end gap-3">
            {period === 'custom' && (
              <>
                <div>
                  <label htmlFor={fromId} className="mb-1 block text-xs font-medium text-muted">
                    From
                  </label>
                  <Input id={fromId} type="date" value={customFrom} max={customTo || today} onChange={(e) => setCustomFrom(e.target.value)} className="w-auto" />
                </div>
                <div>
                  <label htmlFor={toId} className="mb-1 block text-xs font-medium text-muted">
                    To
                  </label>
                  <Input id={toId} type="date" value={customTo} min={customFrom} max={today} onChange={(e) => setCustomTo(e.target.value)} className="w-auto" />
                </div>
              </>
            )}
            <div className="ml-auto flex items-center gap-2 pb-2 text-xs text-muted" aria-live="polite">
              {q.isFetching && data ? <RefreshCcw className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
              {data && (
                <span>
                  {shortDate(data.period.from)} – {shortDate(data.period.to)}
                </span>
              )}
            </div>
            {invalidRange && (
              <p className="w-full text-[0.8125rem] text-danger-ink" role="alert">
                Choose a start date on or before the end date.
              </p>
            )}
          </div>
        )}

        {q.isError && !data ? (
          <Card>
            <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Couldn't load insights" />
          </Card>
        ) : !data ? (
          invalidRange ? null : <InsightsSkeleton />
        ) : (
          <div className={clsx('space-y-5', q.isFetching && 'opacity-80 transition-opacity')}>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile icon={<MessageCircleReply className="h-5 w-5" />} label="Needs my reply" value={data.needsMyReply.length} hint="conversations waiting on you" tone={data.needsMyReply.length ? 'warn' : 'success'} />
              <StatTile icon={<Hourglass className="h-5 w-5" />} label="Stalled" value={data.stalled.length} hint="gone quiet without resolution" tone={data.stalled.length ? 'info' : 'neutral'} />
              <StatTile icon={<MessagesSquare className="h-5 w-5" />} label="New conversations" value={data.changedSinceLastReview.newThreads} hint="since your last review" tone="primary" />
              <StatTile icon={<MailPlus className="h-5 w-5" />} label="New messages" value={data.changedSinceLastReview.newMessages} hint="since your last review" tone="primary" />
            </div>
            <div className="grid gap-5 lg:grid-cols-2">
              <NeedsReplyCard items={data.needsMyReply} />
              <StalledCard items={data.stalled} />
              <TopicsCard insights={data} />
              <ChangedCard changed={data.changedSinceLastReview} />
              <div className="lg:col-span-2">
                <CoverageCard coverage={data.coverage} />
              </div>
            </div>
          </div>
        )}

        <AskPanel />
      </div>
    </Page>
  );
}

function StatTile({ icon, label, value, hint, tone }: { icon: ReactNode; label: string; value: number; hint: string; tone: 'primary' | 'warn' | 'info' | 'success' | 'neutral' }) {
  const tile = {
    primary: 'mp-pill-lavender',
    warn: 'mp-pill-yellow',
    info: 'mp-pill-sky',
    success: 'mp-pill-lime',
    neutral: 'bg-raised text-muted',
  }[tone];
  return (
    <Card className="hover-lift flex items-center gap-4 p-5">
      <span className={clsx('flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl', tile)}>{icon}</span>
      <div className="min-w-0">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">{label}</p>
        <p className="mp-hero mt-0.5 !text-[2rem]">{value.toLocaleString()}</p>
        <p className="mt-1 truncate text-xs text-muted">{hint}</p>
      </div>
    </Card>
  );
}

function InsightsSkeleton() {
  return (
    <div className="space-y-5" aria-busy="true" aria-label="Loading insights">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} className="flex items-center gap-4 p-5">
            <Skeleton className="h-11 w-11 rounded-[13px]" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-6 w-12" />
            </div>
          </Card>
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} className="space-y-4 p-6">
            <Skeleton className="h-5 w-40" />
            <SkeletonLines lines={4} />
          </Card>
        ))}
      </div>
    </div>
  );
}

function ThreadLinkRow({ threadId, subject, meta }: { threadId: string; subject: string; meta: ReactNode }) {
  return (
    <li>
      <Link to={`/inbox/${threadId}`} className="group flex min-h-[44px] items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-raised">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-raised text-muted transition-colors group-hover:bg-primary-soft group-hover:text-primary-ink">
          <Inbox className="h-4 w-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1 truncate text-[0.875rem] font-medium text-ink">{subject || '(no subject)'}</span>
        <span className="shrink-0 text-xs text-muted">{meta}</span>
      </Link>
    </li>
  );
}

function NeedsReplyCard({ items }: { items: InboxInsights['needsMyReply'] }) {
  return (
    <Card>
      <CardHeader
        icon={<MessageCircleReply className="h-5 w-5" />}
        title="Needs my reply"
        description="Conversations where you appear to owe the next reply."
        actions={<Badge tone={items.length ? 'warn' : 'success'}>{items.length}</Badge>}
      />
      <CardBody className="pt-4">
        {items.length === 0 ? (
          <EmptyState className="py-6" title="You're not holding anyone up" description="No imported conversation in this period is waiting on you." />
        ) : (
          <ul className="-mx-3 max-h-80 space-y-0.5 overflow-y-auto scroll-thin">
            {items.map((t) => (
              <ThreadLinkRow key={t.threadId} threadId={t.threadId} subject={t.subject} meta={<span title={fullDate(t.waitingSince)}>since {relativeTime(t.waitingSince)}</span>} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function StalledCard({ items }: { items: InboxInsights['stalled'] }) {
  return (
    <Card>
      <CardHeader
        icon={<Hourglass className="h-5 w-5" />}
        title="Stalled conversations"
        description="Threads that have gone quiet without a clear resolution."
        actions={<Badge tone={items.length ? 'info' : 'neutral'}>{items.length}</Badge>}
      />
      <CardBody className="pt-4">
        {items.length === 0 ? (
          <EmptyState className="py-6" title="Nothing has stalled" description="No imported conversation in this period looks stuck." />
        ) : (
          <ul className="-mx-3 max-h-80 space-y-0.5 overflow-y-auto scroll-thin">
            {items.map((t) => (
              <ThreadLinkRow key={t.threadId} threadId={t.threadId} subject={t.subject} meta={<span title={fullDate(t.lastActivity)}>quiet since {relativeTime(t.lastActivity)}</span>} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function TopicsCard({ insights }: { insights: InboxInsights }) {
  const [open, setOpen] = useState<string | null>(null);
  const topics = insights.topics;
  const max = Math.max(1, ...topics.map((t) => t.count));
  // Topics carry thread ids only; reuse subjects we already know from the other lists.
  const subjects = useMemo(() => {
    const m = new Map<string, string>();
    insights.needsMyReply.forEach((t) => m.set(t.threadId, t.subject));
    insights.stalled.forEach((t) => m.set(t.threadId, t.subject));
    return m;
  }, [insights]);

  return (
    <Card>
      <CardHeader icon={<BarChart3 className="h-5 w-5" />} title="Topics" description="What your imported mail has been about. Select a topic to see its conversations." />
      <CardBody className="pt-4">
        {topics.length === 0 ? (
          <EmptyState className="py-6" title="No topics yet" description="Topics appear once enough imported conversations in this period have been analysed." />
        ) : (
          <ul className="-mx-3 space-y-0.5">
            {topics.map((t) => {
              const expanded = open === t.label;
              const panelId = `topic-${t.label.replace(/\W+/g, '-')}`;
              return (
                <li key={t.label}>
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => setOpen(expanded ? null : t.label)}
                    className={clsx('group block w-full rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-raised', expanded && 'bg-raised')}
                  >
                    <span className="mb-1.5 flex items-center justify-between gap-2 text-[0.8125rem]">
                      <span className="min-w-0 truncate font-medium capitalize text-ink">{t.label}</span>
                      <span className="flex shrink-0 items-center gap-1.5 tabular-nums text-muted">
                        {t.count} <span className="sr-only">conversations</span>
                        <ChevronDown className={clsx('h-4 w-4 transition-transform', expanded && 'rotate-180')} aria-hidden />
                      </span>
                    </span>
                    <span className="block h-1.5 w-full overflow-hidden rounded-full bg-sunken" aria-hidden>
                      <span className="block h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${Math.max(4, (t.count / max) * 100)}%` }} />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {expanded && (
                      <motion.ul
                        id={panelId}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={spring}
                        className="overflow-hidden pl-3"
                      >
                        {t.threadIds.length === 0 ? (
                          <li className="px-3 py-2 text-xs text-muted">No linked conversations.</li>
                        ) : (
                          t.threadIds.map((id, i) => (
                            <li key={id}>
                              <Link to={`/inbox/${id}`} className="flex min-h-[40px] items-center gap-2 rounded-lg px-3 py-1.5 text-[0.8125rem] text-primary-ink transition-colors hover:bg-primary-soft">
                                <Inbox className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                <span className="truncate">{subjects.get(id) ?? `Conversation ${i + 1}`}</span>
                              </Link>
                            </li>
                          ))
                        )}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ChangedCard({ changed }: { changed: InboxInsights['changedSinceLastReview'] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const mark = useMutation({
    mutationFn: () => api('POST /api/insights/reviewed'),
    onSuccess: () => {
      toast.success('Marked as reviewed. Changes will be counted from now.');
      void qc.invalidateQueries({ queryKey: qk.insightsRoot });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
    onError: (e) => toast.error(e),
  });
  return (
    <Card>
      <CardHeader
        icon={<RefreshCcw className="h-5 w-5" />}
        title="Changed since last review"
        description={changed.since ? <span title={fullDate(changed.since)}>Since you last reviewed {relativeTime(changed.since)}</span> : "You haven't marked your inbox as reviewed yet."}
        actions={
          <Button variant="primary" size="sm" icon={<Check className="h-4 w-4" />} loading={mark.isPending} onClick={() => mark.mutate()}>
            Mark reviewed
          </Button>
        }
      />
      <CardBody className="pt-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-hairline bg-raised/60 p-4">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">New conversations</p>
            <p className="mt-1 text-3xl font-semibold tabular-nums text-ink">{changed.newThreads.toLocaleString()}</p>
          </div>
          <div className="rounded-2xl border border-hairline bg-raised/60 p-4">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">New messages</p>
            <p className="mt-1 text-3xl font-semibold tabular-nums text-ink">{changed.newMessages.toLocaleString()}</p>
          </div>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-muted">Marking your inbox as reviewed resets both counters so you can see what arrives next.</p>
      </CardBody>
    </Card>
  );
}

function CoverageCard({ coverage }: { coverage: CoverageReport }) {
  const mailboxes = useMailboxMap();
  return (
    <Card>
      <CardHeader icon={<Database className="h-5 w-5" />} title="Coverage" description="How much of each mailbox has been imported and analysed." />
      <CardBody className="space-y-4 pt-5">
        {coverage.mailboxes.length === 0 ? (
          <EmptyState
            className="py-6"
            icon={<Plug className="h-6 w-6" />}
            title="No mailboxes connected"
            description="Connect an email account to import mail and generate insights."
            actions={
              <Link to="/settings/accounts">
                <Button variant="primary" icon={<Plug className="h-4 w-4" />}>Connect an account</Button>
              </Link>
            }
          />
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {coverage.mailboxes.map((m) => {
              const meta = phaseMeta[m.phase];
              const mb = mailboxes.get(m.mailboxId);
              return (
                <li key={m.mailboxId} className="space-y-3 rounded-2xl border border-hairline bg-raised/40 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <AccountBadge color={mb?.color ?? '#94a3b8'} label={m.email} className="max-w-[18rem] text-[0.8125rem]" />
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                  </div>
                  <dl className="grid grid-cols-2 gap-3 text-[0.8125rem]">
                    <div>
                      <dt className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Imported messages</dt>
                      <dd className="mt-0.5 font-semibold tabular-nums text-ink">{m.importedMessages.toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Date range</dt>
                      <dd className="mt-0.5 font-medium text-ink">{m.oldestImportedAt ? `${shortDate(m.oldestImportedAt)} → ${shortDate(m.newestImportedAt)}` : '—'}</dd>
                    </div>
                  </dl>
                  <div>
                    <p className="mb-1.5 flex justify-between text-xs text-muted">
                      <span>Analysed threads</span>
                      <span className="tabular-nums">
                        {m.analysedThreads.toLocaleString()} / {m.totalThreads.toLocaleString()}
                      </span>
                    </p>
                    <ProgressBar value={m.analysedThreads} max={m.totalThreads || 1} label={`Analysed threads for ${m.email}`} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <p className="text-xs text-subtle">Insights only cover imported messages — MailPilot never claims to have analysed mail it hasn't imported.</p>
      </CardBody>
    </Card>
  );
}
